import * as THREE from 'three';
import {
    OperationGroup,
    Dimension,
    Panel,
    Edge,
    Operation,
} from 'components/customer/Preview3D/types';
import {FontLoader} from 'three/examples/jsm/loaders/FontLoader.js';
import {TextGeometry} from 'three/examples/jsm/geometries/TextGeometry';
import {LineSegments2} from 'three/examples/jsm/lines/LineSegments2.js';
import {LineSegmentsGeometry} from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import {LineMaterial} from 'three/examples/jsm/lines/LineMaterial.js';
import {createHoleMesh, useHolePuncher} from '../lib/useHolePuncher';
import {applyWallFlushOutlineFix} from 'components/customer/Preview3DCommon/shelves/common';

interface RenderLabelProps {
    shelfLabel: string;
    showShelfLabel: boolean;
    shape: THREE.Shape;
    extrudeSettings: object;
    thickness: {exteriorThickness: number; carcaseThickness: number};
    lineMesh: THREE.LineSegments<
        THREE.EdgesGeometry<THREE.ExtrudeGeometry>,
        THREE.LineBasicMaterial,
        THREE.Object3DEventMap
    >;
    isLeftReturnProduct: boolean;
    isRightReturnProduct: boolean;
}

interface GenerateMaterialsProps {
    plainWhiteColor: boolean;
    edgeTexture: THREE.Texture;
    texture: THREE.Texture;
    isExteriorMaterial: boolean;
    shouldApplyEdging: boolean;
    exteriorSubstrateMaterial: THREE.MeshStandardMaterial;
    includeHardware: boolean;
    carcaseSubstrateMaterial: THREE.MeshStandardMaterial;
    hideSgement: boolean;
    partiallyTransparentSegment: boolean;
    nonSupplyCarcase: boolean;
    operationCount: number;
    edging: Edge | null;
    isFrontRangehoodVent: boolean;
    withTopEdging: boolean;
    dynamicExteriorEdging: boolean;
    isEdgedBottom: boolean;
    noEdging: boolean;
    withFrontEdging: boolean;
    flipSubstrate: boolean;
    withBackEdging: boolean;
}

export const vecFromObj = (obj: Dimension) => {
    return new THREE.Vector3(obj.x, obj.y, obj.z);
};

export const planeTransformFromNormal = (
    planeNormal: Dimension,
    part: Panel
) => {
    const planeMatrix = new THREE.Matrix4();
    if (planeNormal.x > 0) {
        planeMatrix.makeBasis(
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(1, 0, 0)
        );
        planeMatrix.setPosition(new THREE.Vector3(part.Length, 0, 0));
    } else if (planeNormal.x < 0) {
        planeMatrix.makeBasis(
            new THREE.Vector3(0, -1, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(-1, 0, 0)
        );
    } else if (planeNormal.y > 0) {
        planeMatrix.makeBasis(
            new THREE.Vector3(-1, 0, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0, 1, 0)
        );
        planeMatrix.setPosition(new THREE.Vector3(0, part.Width, 0));
    } else if (planeNormal.y < 0) {
        planeMatrix.makeBasis(
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(0, -1, 0)
        );
    } else if (planeNormal.z < 0) {
        planeMatrix.makeBasis(
            new THREE.Vector3(1, 0, 0),
            new THREE.Vector3(0, 1, 0),
            new THREE.Vector3(0, 0, -1)
        );
        planeMatrix.setPosition(new THREE.Vector3(0, 0, -part.Thickness));
    }
    return planeMatrix;
};

export const reduceMaterialOpacity = (
    material: THREE.Material,
    opacity = 0.1,
    depthWrite = true
) => {
    if (material) {
        material.transparent = true;
        material.opacity = opacity;
        material.depthWrite = depthWrite;
    }
};

const SEGMENT_LINE_OPACITY = 0.1;

// Debug: skip texture loading so every panel keeps the white fallback
// material, as when a texture fails to load. Keep false outside debugging.
const FORCE_FALLBACK_MATERIAL = false;

// Stencil value marking pixels that already have a faded outline.
const SEGMENT_LINE_STENCIL_REF = 1;

// Fades panel outlines while the solid panels are shown, and makes them fully
// opaque in wireframe mode where the outlines are the only thing drawn.
export const setSegmentLineTransparency = (
    material: THREE.LineBasicMaterial,
    isWireframeMode = false
) => {
    material.transparent = !isWireframeMode;
    material.opacity = isWireframeMode ? 1 : SEGMENT_LINE_OPACITY;

    // Coincident outlines (e.g. panels sharing an edge) would stack their
    // opacity into dark lines, so each pixel takes only the first faded
    // outline drawn on it. Needs a renderer created with `stencil: true`;
    // without a stencil buffer the test always passes.
    material.stencilWrite = !isWireframeMode;
    material.stencilRef = SEGMENT_LINE_STENCIL_REF;
    material.stencilFunc = THREE.NotEqualStencilFunc;
    material.stencilZPass = THREE.ReplaceStencilOp;

    material.needsUpdate = true;
};

export const attachSegmentLineWireframeToggle = (
    lineSegments: THREE.LineSegments<
        THREE.BufferGeometry,
        THREE.LineBasicMaterial
    >
) => {
    lineSegments.userData.onToggleWireframe = (isWireframeMode: boolean) => {
        setSegmentLineTransparency(lineSegments.material, isWireframeMode);
    };
};

export const addLabelMesh = (
    shelfLabel: string,
    size: THREE.Vector3,
    thickness: number,
    parentMesh:
        | THREE.Mesh
        | THREE.LineSegments<
              THREE.EdgesGeometry<THREE.ExtrudeGeometry>,
              THREE.LineBasicMaterial,
              THREE.Object3DEventMap
          >,
    isLeftReturnProduct: boolean,
    isRightReturnProduct: boolean
) => {
    const fontLoader = new FontLoader();

    fontLoader.load(
        '/templates/3D/fonts/Poppins SemiBold_Regular.json',
        (font) => {
            const textGeometry = new TextGeometry(shelfLabel, {
                font,
                size: 4,
                depth: 0.5,
            });

            const textMesh = new THREE.Mesh(
                textGeometry,
                new THREE.MeshStandardMaterial({
                    color: '#333',
                })
            );

            const scale = 7;
            textMesh.scale.set(scale, scale, scale);

            const boundingBox = new THREE.Box3().setFromObject(textMesh);
            const meshWidth = boundingBox.max.x - boundingBox.min.x;

            textMesh.rotation.x = -(90 * Math.PI) / 180;

            let positionX = size.x - meshWidth / 2;

            if (isRightReturnProduct) positionX = 10;
            if (isLeftReturnProduct) positionX = size.x * 2 - meshWidth - 10;

            textMesh.position.set(positionX, size.y * 2 - 20, -thickness - 3.5);

            parentMesh.add(textMesh);
        }
    );
};

const HIGHLIGHT_COLOR = 'rgb(13, 110, 253)';

// Selection box colour of the selected cabinet.
export const SELECTED_CABINET_COLOR = '#45aaf2';

// Selection box colour of a cabinet that overlaps another product or a wall.
export const OVERLAP_CABINET_COLOR = '#ff3f34';

// Scratch objects for the glow, created on first use rather than at import
// time: several test suites mock `three` as an empty module.
let _glowBox: THREE.Box3 | null = null;
let _glowSize: THREE.Vector3 | null = null;

// Coloured light spilled from a highlighted cabinet onto the floor, walls and
// neighbouring products. Distances are in world units.
const GLOW_LIGHT = {
    intensity: 1.5,
    // Light reach as a multiple of the cabinet's bounding-box diagonal.
    rangeScale: 1,
    // Fraction of the remaining intensity change applied per frame.
    fade: 0.2,
    // Lights that can glow at once (e.g. a dragged cabinet and a snap
    // target).
    poolSize: 2,
};

type GlowLightEntry = {
    light: THREE.PointLight;
    owner: THREE.Object3D | null;
    localCenter: THREE.Vector3;
    lastFrame: number;
};

type GlowLightPool = {entries: GlowLightEntry[]; frame: number};

// Adding or removing lights makes three.js recompile every lit material, so
// each scene keeps a fixed pool of lights that just fade to 0 when idle.
const glowLightPools = new WeakMap<THREE.Scene, GlowLightPool>();

const updateGlowLights = (pool: GlowLightPool) => {
    pool.frame++;

    pool.entries.forEach((entry) => {
        // Glows stamp their light while they render, so a light whose glow
        // was hidden, removed or deleted fades out on its own.
        const isActive =
            entry.owner !== null && pool.frame - entry.lastFrame <= 1;
        const target = isActive ? GLOW_LIGHT.intensity : 0;

        entry.light.intensity +=
            (target - entry.light.intensity) * GLOW_LIGHT.fade;

        if (!isActive && entry.light.intensity < 0.01) {
            entry.light.intensity = 0;
            entry.owner = null;
        }
    });
};

/**
 * Adds the glow light pool to `scene`, creating it on first use. Call after
 * every scene.clear() (e.g. when the room is rebuilt), which drops the lights.
 * Cabinets in a scene without a pool are highlighted without a glow.
 * @param {THREE.Scene} scene The scene to light.
 */
export const setupGlowLights = (scene: THREE.Scene) => {
    let pool = glowLightPools.get(scene);

    if (!pool) {
        const newPool: GlowLightPool = {
            frame: 0,
            entries: Array.from(
                {length: GLOW_LIGHT.poolSize},
                (): GlowLightEntry => {
                    // decay 0: even light within range, softly cut off at
                    // `distance`, instead of physically based falloff.
                    const light = new THREE.PointLight(0xffffff, 0, 0, 0);
                    light.name = 'segmentGlowLight';
                    return {
                        light,
                        owner: null,
                        localCenter: new THREE.Vector3(),
                        lastFrame: -Infinity,
                    };
                }
            ),
        };
        pool = newPool;
        glowLightPools.set(scene, newPool);

        // Runs before three.js sets up the frame's lights.
        type OnBeforeRender = THREE.Scene['onBeforeRender'];
        const previousOnBeforeRender = scene.onBeforeRender.bind(
            scene
        ) as OnBeforeRender;
        scene.onBeforeRender = (...args: Parameters<OnBeforeRender>) => {
            previousOnBeforeRender(...args);
            updateGlowLights(newPool);
        };
    }

    // Re-add the lights the last scene.clear() dropped.
    pool.entries.forEach(({light}) => {
        if (light.parent !== scene) scene.add(light);
    });
};

/**
 * Keeps a pooled light glowing in `color` around the cabinet that `mesh`
 * belongs to. Called every frame the glow is drawn.
 * @param {THREE.Object3D} mesh Highlighted panel mesh.
 * @param {THREE.Color} color Glow colour.
 */
const stampGlowLight = (mesh: THREE.Object3D, color: THREE.Color) => {
    // The cabinet root is the ancestor sitting directly in the scene.
    let root: THREE.Object3D = mesh;
    while (root.parent && !(root.parent as THREE.Scene).isScene) {
        root = root.parent;
    }
    const scene = root.parent as THREE.Scene | null;
    if (!scene?.isScene) return;

    const pool = glowLightPools.get(scene);
    if (!pool) return;

    const entry =
        pool.entries.find(({owner}) => owner === root) ??
        pool.entries.find(({owner}) => owner === null);
    if (!entry) return;

    if (entry.owner !== root) {
        entry.owner = root;
        entry.light.intensity = 0;

        _glowBox ??= new THREE.Box3();
        _glowSize ??= new THREE.Vector3();

        _glowBox.setFromObject(root);
        _glowBox.getCenter(entry.localCenter);
        root.worldToLocal(entry.localCenter);
        entry.light.distance =
            _glowBox.getSize(_glowSize).length() * GLOW_LIGHT.rangeScale;
    }

    entry.lastFrame = pool.frame;
    entry.light.color.copy(color);
    // Follow the cabinet while it is dragged or rotated.
    entry.light.position.copy(entry.localCenter).applyMatrix4(root.matrixWorld);
};

// Selection box drawn around a highlighted cabinet's outer bounds. Line
// width is in screen pixels; plain LineBasicMaterial is always 1px in WebGL,
// so the box uses LineSegments2.
const SELECTION_OUTLINE_LINE_WIDTH = 2.5;

type CabinetOutline = {
    group: THREE.Group;
    material: LineMaterial;
    // Highlighted panels of this cabinet; the box shows while any are.
    panels: Set<THREE.Object3D>;
};

const cabinetOutlines = new WeakMap<THREE.Object3D, CabinetOutline>();

// Unit-cube edges, scaled to each cabinet's bounds. Created on first use:
// several test suites mock `three` as an empty module.
let _unitBoxEdges: LineSegmentsGeometry | null = null;
let _outlineResolution: THREE.Vector2 | null = null;

/**
 * The cabinet a panel belongs to: its ancestor sitting directly in the scene.
 * @param {THREE.Object3D} panel Panel mesh.
 * @return {THREE.Object3D | null} Cabinet root, or null if not in a scene.
 */
const getCabinetRoot = (panel: THREE.Object3D) => {
    let root = panel;
    while (root.parent && !(root.parent as THREE.Scene).isScene) {
        root = root.parent;
    }
    return (root.parent as THREE.Scene | null)?.isScene ? root : null;
};

/**
 * Bounds of everything in the cabinet, in the cabinet's own space, so the box
 * stays aligned with the cabinet when it is rotated.
 * @param {THREE.Object3D} root Cabinet root.
 * @param {THREE.Box3} target Receives the bounds.
 * @return {THREE.Box3} The bounds.
 */
const getCabinetLocalBounds = (root: THREE.Object3D, target: THREE.Box3) => {
    target.makeEmpty();
    root.updateWorldMatrix(true, true);

    const rootInverse = root.matrixWorld.clone().invert();
    const toRoot = new THREE.Matrix4();
    const childBounds = new THREE.Box3();

    root.traverse((child) => {
        if (child.userData.isSelectionOutline) return;

        const {geometry} = child as THREE.Mesh | THREE.LineSegments;
        if (!geometry) return;

        if (!geometry.boundingBox) geometry.computeBoundingBox();
        if (!geometry.boundingBox || geometry.boundingBox.isEmpty()) return;

        toRoot.multiplyMatrices(rootInverse, child.matrixWorld);
        target.union(
            childBounds.copy(geometry.boundingBox).applyMatrix4(toRoot)
        );
    });

    return target;
};

/**
 * Creates the (hidden) selection box for a cabinet.
 * @param {THREE.Object3D} root Cabinet root.
 * @return {CabinetOutline} The cabinet's outline.
 */
const createCabinetOutline = (root: THREE.Object3D): CabinetOutline => {
    if (!_unitBoxEdges) {
        const box = new THREE.BoxGeometry(1, 1, 1);
        const edges = new THREE.EdgesGeometry(box);
        _unitBoxEdges = new LineSegmentsGeometry().fromEdgesGeometry(edges);
        box.dispose();
        edges.dispose();
        // An empty bounding box keeps the selection box out of
        // Box3.setFromObject(cabinet), which placement, overlap and width
        // code relies on. Culling and picking are disabled for it anyway.
        _unitBoxEdges.boundingBox = new THREE.Box3();
    }

    const material = new LineMaterial({
        linewidth: SELECTION_OUTLINE_LINE_WIDTH,
        // Drawn over the cabinet, so the back edges show through as well.
        depthTest: false,
        depthWrite: false,
        transparent: true,
        // Cabinets can be mirrored with a negative scale, which reverses the
        // line quads' winding.
        side: THREE.DoubleSide,
    });

    const line = new LineSegments2(_unitBoxEdges, material);
    line.userData.isSelectionOutline = true;
    line.renderOrder = 999;
    line.frustumCulled = false;
    // Never let the box intercept cabinet picking.
    line.raycast = () => undefined;
    line.onBeforeRender = (renderer) => {
        // LineMaterial needs the viewport size to draw pixel widths.
        _outlineResolution ??= new THREE.Vector2();
        renderer.getSize(_outlineResolution);
        material.resolution.copy(_outlineResolution);

        // Keeps the cabinet's coloured light on while the box is drawn.
        stampGlowLight(root, material.color);
    };

    const group = new THREE.Group();
    group.name = 'cabinetSelectionOutline';
    group.userData.isSelectionOutline = true;
    group.visible = false;
    group.add(line);
    // Ghost / preview cabinets are made with clone(), which drops the
    // handlers that hide the box again. Leave it out of clones entirely.
    group.clone = () => {
        const placeholder = new THREE.Group();
        placeholder.visible = false;
        return placeholder;
    };

    return {group, material, panels: new Set()};
};

/**
 * Shows a cabinet's selection box in `color` on behalf of one of its panels.
 * Also used by placeholders so they highlight exactly like cabinets.
 * @param {THREE.Object3D} panel Highlighted panel mesh.
 * @param {THREE.ColorRepresentation} color Selection colour.
 */
export const showCabinetOutline = (
    panel: THREE.Object3D,
    color: THREE.ColorRepresentation
) => {
    const root = getCabinetRoot(panel);
    if (!root) return;

    let outline = cabinetOutlines.get(root);
    if (!outline) {
        outline = createCabinetOutline(root);
        cabinetOutlines.set(root, outline);
    }

    // Fit the box when it appears, so it matches the cabinet's current size.
    if (!outline.group.visible) {
        const bounds = getCabinetLocalBounds(root, new THREE.Box3());
        if (bounds.isEmpty()) return;

        const size = bounds.getSize(new THREE.Vector3());
        bounds.getCenter(outline.group.position);
        outline.group.scale.set(
            Math.max(size.x, 1e-3),
            Math.max(size.y, 1e-3),
            Math.max(size.z, 1e-3)
        );
        // Appending is safe during triggerEvents' traversal (the loop length
        // is cached); re-adding an attached child would reorder the array.
        if (outline.group.parent !== root) root.add(outline.group);
        outline.group.visible = true;
    }

    outline.panels.add(panel);
    outline.material.color.set(color);
};

/**
 * Releases one panel's hold on its cabinet's selection box, hiding it once
 * no panel of the cabinet is highlighted.
 * @param {THREE.Object3D} panel Panel mesh.
 */
export const hideCabinetOutline = (panel: THREE.Object3D) => {
    const root = getCabinetRoot(panel);
    const outline = root && cabinetOutlines.get(root);
    if (!outline) return;

    outline.panels.delete(panel);
    if (outline.panels.size === 0) {
        const {group} = outline;
        group.visible = false;

        // Detached while hidden, so it never skews the cabinet's bounds.
        // Deferred: this runs from event handlers inside
        // triggerEvents' cabinet.traverse(), and Object3D.traverse caches
        // the children count, so removing a child mid-walk would leave an
        // undefined entry and throw.
        queueMicrotask(() => {
            if (!group.visible) group.removeFromParent();
        });
    }
};

/**
 * Installs the highlight handlers (select / drag / snap / overlap colours on
 * the shared box outline + glow) on one panel of a product. Cabinet panels
 * get it from the renderers; placeholders reuse it via
 * RoomPlanner/helpers/placeholder/highlight.ts.
 * @param {THREE.Object3D} mesh Panel (or any product part) to highlight through.
 * @param {THREE.LineBasicMaterial=} segmentMaterial Unused; kept for call-site compatibility.
 */
export const addDragEffectOnClick = (
    mesh: THREE.Object3D,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for call-site compatibility; highlights now draw a box around the whole cabinet instead of recolouring the panel outlines
    segmentMaterial?: THREE.LineBasicMaterial
) => {
    // Highlight states box the whole cabinet and light up its surroundings
    // in the state's colour; the panel outlines stay unchanged.
    const showGlow = (color: THREE.ColorRepresentation) =>
        showCabinetOutline(mesh, color);

    const hideGlow = () => hideCabinetOutline(mesh);

    mesh.userData.onClick = (visible: boolean) => {
        // mesh.visible = visible;
        if (visible) hideGlow();
        else showGlow(HIGHLIGHT_COLOR);
    };

    mesh.userData.onSnap = (visible: boolean) => {
        showGlow(visible ? '#4caf50' : HIGHLIGHT_COLOR);
    };

    mesh.userData.onCabinetSelect = (visible: boolean) => {
        showGlow(visible ? SELECTED_CABINET_COLOR : HIGHLIGHT_COLOR);
    };

    mesh.userData.onOverlap = (visible: boolean) => {
        showGlow(visible ? OVERLAP_CABINET_COLOR : HIGHLIGHT_COLOR);
    };

    mesh.userData.onToggleWireframe = (isWireframe: boolean) => {
        mesh.visible = !isWireframe;
    };

    mesh.userData.setGhostCabinet = (visible: boolean) => {
        mesh.visible = visible;
        if (!visible) hideGlow();
    };

    mesh.userData.onCabinetOverlapsWhileDragging = () => {
        showGlow(OVERLAP_CABINET_COLOR);
    };

    mesh.userData.resetSegmentColor = () => {
        hideGlow();
    };
};

export const renderLabel = ({
    shelfLabel,
    showShelfLabel,
    shape,
    extrudeSettings,
    thickness,
    lineMesh,
    isLeftReturnProduct,
    isRightReturnProduct,
}: RenderLabelProps) => {
    if (shelfLabel && showShelfLabel) {
        const extrudeGeometryForBox = new THREE.ExtrudeGeometry(shape, {
            ...extrudeSettings,
        });
        const box = new THREE.Box3().setFromObject(
            new THREE.Mesh(extrudeGeometryForBox)
        );
        const size = new THREE.Vector3();
        box.getSize(size);
        box.getCenter(size);

        addLabelMesh(
            shelfLabel,
            size,
            thickness.carcaseThickness,
            lineMesh,
            isLeftReturnProduct,
            isRightReturnProduct
        );
    }
};

export const generateDefaultShape = (operation: Operation) => {
    const shape = new THREE.Shape();

    for (const segment of operation.Segments) {
        const bulge = Math.abs(segment.StartCoor.w);
        const startPoint = vecFromObj(segment.StartCoor);
        const endPoint = vecFromObj(segment.EndCoor);

        if (bulge === 0) {
            // geometry.vertices.push(start_point, end_point);
            shape.moveTo(startPoint.x, startPoint.y);
            shape.lineTo(endPoint.x, endPoint.y);
        } else {
            // Line from start to end
            const line = endPoint.clone().sub(startPoint);
            // Midpoint between start and end
            const midpoint = line.clone().multiplyScalar(0.5).add(startPoint);
            // Arc direction is defined by the sign of the w component
            const clockwise = segment.StartCoor.w < 0;

            // Normalised tangent from start to end
            const tangent = line.clone().normalize();
            // Counter clockwise normal
            const normal = new THREE.Vector2(-tangent.y, tangent.x);
            // Flip normal if clockwise
            if (clockwise) {
                normal.negate();
            }

            const sagitta = (line.length() / 2) * bulge;
            const radius =
                (sagitta * sagitta +
                    (line.length() / 2) * (line.length() / 2)) /
                (2 * sagitta);

            // Distance from the line midpoint to the arc center
            const distanceToCenter = radius - sagitta;
            const center = normal
                .clone()
                .multiplyScalar(distanceToCenter)
                .add(midpoint);

            const startPoint2d = new THREE.Vector2(startPoint.x, startPoint.y);
            const endPoint2d = new THREE.Vector2(endPoint.x, endPoint.y);
            const startAngle = startPoint2d.clone().sub(center).angle();
            const endAngle = endPoint2d.clone().sub(center).angle();

            shape.absarc(
                center.x,
                center.y,
                radius,
                startAngle,
                endAngle,
                clockwise
            );
        }
    }

    return shape;
};

const generateBoxGeometry = ({
    extrudeGeometry,
}: {
    extrudeGeometry: THREE.ExtrudeGeometry;
}) => {
    // Reuse the existing extrudeGeometry instead of creating a new one
    const box = new THREE.Box3().setFromObject(new THREE.Mesh(extrudeGeometry));

    const size = new THREE.Vector3();
    box.getSize(size);

    const boxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    box.getCenter(size);

    return {boxGeometry, size};
};

const _textureCache = new Map<string, THREE.Texture>();

// Tracks in-flight loads so concurrent callers for the same URL share one
// promise instead of each starting an independent TextureLoader request.
const _loadingPromises = new Map<string, Promise<THREE.Texture>>();

// Texture handles returned synchronously by TextureLoader.load() while decode
// is still in progress — lets materials bindTextureWhenReady on the same object.
const _loadingTextures = new Map<string, THREE.Texture>();

let _placeholderTexture: THREE.DataTexture | null = null;

// Texture loads and material swaps still in progress, so callers that need
// a fully textured frame (e.g. PDF snapshots) can wait for them.
const _pendingTextureWork = new Set<Promise<unknown>>();

const trackTextureWork = <T>(work: Promise<T>): Promise<T> => {
    _pendingTextureWork.add(work);
    work.finally(() => _pendingTextureWork.delete(work)).catch(
        (): void => undefined
    );
    return work;
};

/**
 * Resolves once every in-flight texture load and material swap has settled,
 * including work started while waiting, or once `timeoutMs` has passed.
 * @param {number} [timeoutMs] Upper bound on the wait.
 * @return {Promise<boolean>} False if it timed out with work still pending.
 */
export const waitForPendingTextures = async (
    timeoutMs = 15000
): Promise<boolean> => {
    const deadline = Date.now() + timeoutMs;

    while (_pendingTextureWork.size > 0) {
        const remaining = deadline - Date.now();
        if (remaining <= 0) return false;

        await Promise.race([
            Promise.allSettled(Array.from(_pendingTextureWork)),
            new Promise((resolve) => setTimeout(resolve, remaining)),
        ]);
    }

    return true;
};

export const isTextureReady = (
    texture: THREE.Texture | null | undefined
): boolean => Boolean(texture && (texture.image || texture.version > 0));

/** 1×1 white texture used while real material images are still decoding.
 * @return {THREE.Texture} Shared white placeholder texture.
 */
export const getPlaceholderTexture = (): THREE.Texture => {
    if (!_placeholderTexture) {
        const data = new Uint8Array([255, 255, 255, 255]);
        _placeholderTexture = new THREE.DataTexture(data, 1, 1);
        _placeholderTexture.colorSpace = THREE.SRGBColorSpace;
        _placeholderTexture.needsUpdate = true;
    }
    return _placeholderTexture;
};

/**
 * Returns the cached or in-flight texture handle for a URL, or kicks off a
 * load and returns the handle immediately (image may not be ready yet).
 * @param {string} url Texture URL.
 * @param {THREE.TextureLoader} [textureLoader] Optional loader instance.
 * @return {THREE.Texture} Texture handle safe to assign to a material map.
 */
export const resolveTexture = (
    url: string,
    textureLoader?: THREE.TextureLoader
): THREE.Texture => {
    const cached = _textureCache.get(url);
    if (cached) return cached;

    const loading = _loadingTextures.get(url);
    if (loading) return loading;

    void loadBaseTexture(url, textureLoader);
    return _loadingTextures.get(url) ?? getPlaceholderTexture();
};

/**
 * Assigns placeholder map immediately, then swaps to the real texture once
 * decoded — prevents black faces on slow / throttled GPUs without blocking.
 * @param {THREE.MeshStandardMaterial | THREE.MeshPhongMaterial} material Target material.
 * @param {THREE.Texture | null | undefined} texture Texture to bind when ready.
 * @param {Function} [configure] Optional callback to set wrap / repeat / rotation.
 * Applied to a clone, since `texture` is usually the shared cached handle.
 */
export const bindTextureWhenReady = (
    material: THREE.MeshStandardMaterial | THREE.MeshPhongMaterial,
    texture: THREE.Texture | null | undefined,
    configure?: (texture: THREE.Texture) => void
): void => {
    if (!texture) {
        material.map = getPlaceholderTexture();
        material.needsUpdate = true;
        return;
    }

    // Configuring the cached texture in place would leak its repeat / offset
    // into every later loadTexture() clone (e.g. carcase panels sampling a
    // tiny patch of the image and rendering as one flat colour).
    const bind = () => {
        const target = configure ? cloneTextureForMutation(texture) : texture;
        configure?.(target);
        material.map = target;
        material.needsUpdate = true;
    };

    if (isTextureReady(texture)) {
        bind();
        return;
    }

    material.map = getPlaceholderTexture();
    material.needsUpdate = true;

    void trackTextureWork(waitForTextureLoad(texture).then(bind));
};

const getReadySubstrateMaterial = (
    material: THREE.MeshStandardMaterial | null
): THREE.MeshStandardMaterial | null => {
    if (!material) return null;
    if (!material.map || isTextureReady(material.map)) return material;

    const placeholderSubstrate = new THREE.MeshStandardMaterial();
    bindTextureWhenReady(placeholderSubstrate, material.map);
    return placeholderSubstrate;
};

export const clearTextureCache = (): void => {
    _textureCache.clear();
    _loadingPromises.clear();
    _loadingTextures.clear();
};

const cloneTextureForMutation = (texture: THREE.Texture): THREE.Texture => {
    const cloned = texture.clone();
    cloned.needsUpdate = true;
    return cloned;
};

const usesSceneLoadingManager = (
    textureLoader?: THREE.TextureLoader
): textureLoader is THREE.TextureLoader =>
    Boolean(
        textureLoader?.manager &&
        textureLoader.manager !== THREE.DefaultLoadingManager
    );

export const notifyManagerInstantLoad = (
    textureLoader: THREE.TextureLoader | undefined,
    url: string
): void => {
    if (!usesSceneLoadingManager(textureLoader)) return;
    textureLoader.manager.itemStart(url);
    // Defer itemEnd (same as ImageLoader cache path) so onLoad does not fire
    // while createSceneXML is still iterating operation groups.
    queueMicrotask(() => textureLoader.manager.itemEnd(url));
};

const trackInflightWithManager = (
    textureLoader: THREE.TextureLoader | undefined,
    url: string,
    promise: Promise<THREE.Texture>
): Promise<THREE.Texture> => {
    if (!usesSceneLoadingManager(textureLoader)) return promise;
    textureLoader.manager.itemStart(url);
    return promise.finally(() => textureLoader.manager.itemEnd(url));
};

const loadBaseTexture = (
    url: string,
    textureLoader?: THREE.TextureLoader
): Promise<THREE.Texture> => {
    const cached = _textureCache.get(url);
    if (cached) {
        notifyManagerInstantLoad(textureLoader, url);
        return Promise.resolve(cached);
    }

    const inflight = _loadingPromises.get(url);
    if (inflight !== undefined) {
        return trackInflightWithManager(textureLoader, url, inflight);
    }

    const promise = (async () => {
        const loader = textureLoader ?? new THREE.TextureLoader();

        let rejectLoad: ((err: unknown) => void) | undefined;
        const failed = new Promise<never>((_, reject) => {
            rejectLoad = reject;
        });

        const handle = loader.load(url, undefined, undefined, (err) =>
            rejectLoad?.(err)
        );
        _loadingTextures.set(url, handle);

        await Promise.race([
            failed,
            (async () => {
                if (!isTextureReady(handle)) {
                    await waitForTextureLoad(handle);
                }
            })(),
        ]);

        _textureCache.set(url, handle);
        _loadingPromises.delete(url);
        _loadingTextures.delete(url);
        return handle;
    })().catch((err) => {
        _loadingPromises.delete(url);
        _loadingTextures.delete(url);
        throw err;
    });

    _loadingPromises.set(url, promise);
    void trackTextureWork(promise);
    return promise;
};

export const loadCachedTexture = (url: string): Promise<THREE.Texture> =>
    loadBaseTexture(url);

export const waitForTextureLoad = (
    texture: THREE.Texture | null | undefined
): Promise<void> => {
    if (!texture) return Promise.resolve();
    // texture.image is the getter for texture.source.data in Three.js ≥ r152
    if (texture.image) return Promise.resolve();
    // needsUpdate may have fired before we install the interceptor (e.g. the
    // shared MDF substrate texture in createSceneXML). version > 0 means the
    // decode already completed — do not wait for the 5 s fallback timeout.
    if (texture.version > 0) return Promise.resolve();

    return new Promise<void>((resolve) => {
        let settled = false;
        let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

        const finish = () => {
            if (settled) return;
            settled = true;
            if (fallbackTimer !== null) {
                clearTimeout(fallbackTimer);
                fallbackTimer = null;
            }
            // Remove the own-property override to restore the prototype setter.
            try {
                delete (texture as unknown as Record<string, unknown>)
                    .needsUpdate;
            } catch {
                // Non-configurable in rare edge cases — harmless to ignore.
            }
            resolve();
        };

        // Shadow the prototype `set needsUpdate` on this instance only.
        // THREE.TextureLoader calls `texture.needsUpdate = true` exactly once
        // when the decoded image is assigned.
        const protoDescriptor = Object.getOwnPropertyDescriptor(
            Object.getPrototypeOf(texture) as object,
            'needsUpdate'
        );
        Object.defineProperty(texture, 'needsUpdate', {
            set(value: boolean) {
                protoDescriptor?.set?.call(this, value);
                if (value) finish();
            },
            configurable: true,
        });

        // Double-check: the image might have arrived between our `.image` check
        // above and installing the interceptor.
        if (texture.image || texture.version > 0) {
            finish();
            return;
        }

        // Hard timeout so a 404 / network failure never stalls the mesh.
        fallbackTimer = setTimeout(finish, 5000);
    });
};

export const loadTexture = (
    textureLoader: THREE.TextureLoader,
    url: string | null | undefined
): Promise<THREE.Texture> => {
    if (!url) {
        return Promise.reject(new Error('No texture URL provided'));
    }

    return loadBaseTexture(url, textureLoader).then(cloneTextureForMutation);
};

const generateMaterials = ({
    plainWhiteColor,
    edgeTexture,
    texture,
    isExteriorMaterial,
    shouldApplyEdging,
    exteriorSubstrateMaterial,
    includeHardware,
    carcaseSubstrateMaterial,
    hideSgement,
    partiallyTransparentSegment,
    nonSupplyCarcase,
    operationCount,
    edging,
    isFrontRangehoodVent,
    withTopEdging,
    dynamicExteriorEdging,
    isEdgedBottom,
    noEdging,
    withFrontEdging,
    flipSubstrate,
    withBackEdging,
}: GenerateMaterialsProps) => {
    const edgeMaterial = new THREE.MeshStandardMaterial(
        plainWhiteColor
            ? {
                  color: '#F7F8F5',
              }
            : {
                  map: edgeTexture || texture,
              }
    );
    const faceMaterial = new THREE.MeshStandardMaterial(
        plainWhiteColor
            ? {
                  color: '#F7F8F5',
              }
            : {
                  map: texture,
              }
    );

    const exteriorSubstrate = getReadySubstrateMaterial(
        exteriorSubstrateMaterial
    );
    const carcaseSubstrate = getReadySubstrateMaterial(
        carcaseSubstrateMaterial
    );

    const substrateMaterial = isExteriorMaterial
        ? shouldApplyEdging
            ? edgeMaterial
            : exteriorSubstrate
        : plainWhiteColor
          ? includeHardware
              ? new THREE.MeshStandardMaterial({
                    color: '#F7F8F5',
                })
              : null
          : carcaseSubstrate;

    const computedExteriorSubstrateMaterial = shouldApplyEdging
        ? edgeMaterial
        : exteriorSubstrate;
    const topSubstrateMaterial = substrateMaterial?.clone();
    // cloning so we don't impact others using this material
    const substrateMaterialClone = substrateMaterial?.clone();

    if (hideSgement) {
        reduceMaterialOpacity(
            faceMaterial,
            partiallyTransparentSegment ? 0.8 : undefined
        );
        reduceMaterialOpacity(
            edgeMaterial,
            partiallyTransparentSegment ? 0.8 : undefined
        );
        reduceMaterialOpacity(
            topSubstrateMaterial,
            partiallyTransparentSegment ? 0.8 : undefined
        );
        reduceMaterialOpacity(
            substrateMaterialClone,
            partiallyTransparentSegment ? 0.8 : undefined
        );
    }

    if (nonSupplyCarcase) {
        reduceMaterialOpacity(faceMaterial, 0.3, false);
        reduceMaterialOpacity(edgeMaterial, 0.3, false);
        reduceMaterialOpacity(topSubstrateMaterial, 0.3, false);
        reduceMaterialOpacity(substrateMaterialClone, 0.3, false);
    }

    let edgingLocal = {...edging};

    if (operationCount > 3) {
        edgingLocal = null;
    }

    if (dynamicExteriorEdging) {
        edgingLocal = {...edging};
    }

    // For top substrate
    let topColor = edgingLocal
        ? edgingLocal.w1
            ? edgeMaterial
            : isExteriorMaterial
              ? computedExteriorSubstrateMaterial
              : substrateMaterialClone
        : // Edges the right side of the front vent panel in rangehood
          isFrontRangehoodVent
          ? edgeMaterial
          : topSubstrateMaterial;

    if (withTopEdging) {
        topColor = edgeMaterial;
    }

    // For bottom substrate
    const bottomColor = isEdgedBottom
        ? edgeMaterial
        : edgingLocal
          ? edgingLocal.w2
              ? edgeMaterial
              : isExteriorMaterial
                ? computedExteriorSubstrateMaterial
                : substrateMaterialClone
          : substrateMaterialClone;

    // For front substrate
    let frontColor = noEdging
        ? substrateMaterialClone
        : edgingLocal
          ? edgingLocal.l1
              ? edgeMaterial
              : isExteriorMaterial
                ? computedExteriorSubstrateMaterial
                : substrateMaterialClone
          : edgeMaterial;

    if (withFrontEdging) {
        frontColor = edgeMaterial;
    }

    // For back substrate
    let backColor = edgingLocal
        ? edgingLocal.l2
            ? edgeMaterial
            : isExteriorMaterial
              ? computedExteriorSubstrateMaterial
              : substrateMaterialClone
        : substrateMaterialClone;

    if (withBackEdging) {
        backColor = edgeMaterial;
    }

    return [
        topColor,
        bottomColor,
        flipSubstrate ? backColor : frontColor,
        flipSubstrate ? frontColor : backColor,
        faceMaterial,
        faceMaterial,
    ];
};

export const createPanelGeometry = (
    manager: THREE.LoadingManager,
    operationsGroup: OperationGroup,
    color = '#000000',
    showTexture = false,
    materialImage: string | null = null,
    carcaseMaterialImage: string | null = null,
    materialEdgeImage: string | null = null,
    carcaseMaterialEdgeImage: string | null = null,
    shouldApplyMaterial = false,
    isExteriorMaterial = false,
    horizontalGrainExt = false,
    horizontalGrainCarc = false,
    edging: Edge | null = null,
    nonSupplyCarcase = false,
    plainWhiteColor = false,
    thickness: {
        exteriorThickness: number;
        carcaseThickness: number;
        centerOffset?: number;
    } = {
        exteriorThickness: 16.5,
        carcaseThickness: 16.5,
    },
    hideSgement = false,
    partiallyTransparentSegment = false,
    shelfLabel = '',
    showShelfLabel = false,
    carcaseSubstrateMaterial: THREE.MeshStandardMaterial = null,
    exteriorSubstrateMaterial: THREE.MeshStandardMaterial = null,
    includeHardware = false,
    isLeftReturnProduct = false,
    isRightReturnProduct = false,
    operationCount = 0,
    isEdgedBottom = false,
    noEdging = false,
    shouldApplyEdgingParam = false,
    flipSubstrate = false,
    isFrontRangehoodVent = false,
    withTopEdging = false,
    withFrontEdging = false,
    dynamicExteriorEdging = false,
    isWireframeMode = false,
    withBackEdging = false
) => {
    const shouldApplyEdging = shouldApplyEdgingParam && !dynamicExteriorEdging;
    const textureLoader = new THREE.TextureLoader(manager);

    const segmentMaterial = new THREE.LineBasicMaterial({
        color,
    });
    // Hidden / non-supply segments set their own opacity further down, and
    // without textures the outlines are the only thing drawn.
    const fadeSegmentLines = showTexture && !hideSgement && !nonSupplyCarcase;

    if (fadeSegmentLines) {
        setSegmentLineTransparency(segmentMaterial, isWireframeMode);
    }

    const panelGroup = new THREE.Group();

    let hasPerimeterShape = false;
    for (const operation of operationsGroup.Operations) {
        if (operation.Type === 'perimeter_shape') {
            hasPerimeterShape = true;
        }
    }

    for (const operation of operationsGroup.Operations) {
        if (operation.Type === 'panel' && hasPerimeterShape) {
            continue;
        }

        if (operation.Type === 'system32') {
            const config = {
                holeRadius: 2.5, // 5mm diameter = 2.5mm radius
                holeSpacing: 32, // 32mm spacing between hole centers
                holeOffset: 37, // 37mm from the front edge to the centers of all holes
                backsideOffset: 58, // 50mm on the backside + 8mm for the panel thickness
                bottomHoleOffset: 40, // 32mm up from the bottom + 8mm
                holeDepth: 12, // 12mm depth for the holes
            };
            useHolePuncher(operation, showTexture, panelGroup, config);
            continue;
        }

        if (operation.Type === 'drilled_hole') {
            const {x, y, z: depth} = operation.Circle.Center;
            const radius = operation.Circle.Diameter / 2;

            const [hole, tip] = createHoleMesh(
                showTexture,
                radius,
                depth,
                carcaseSubstrateMaterial
            );

            hole.position.set(x, y, depth / 2 + 0.25);
            hole.rotation.x = Math.PI / 2;

            tip.position.set(x, y, 0.25);
            tip.rotation.x = Math.PI / 2;

            panelGroup.add(hole, tip);
            continue;
        }

        if (operation.Segments !== undefined) {
            const shape = generateDefaultShape(operation);

            const extrudeSettings = {
                curveSegments: operationCount > 16 ? 8 : 24, // this is base on the operation count rendered fast for large jobs
                depth: isExteriorMaterial
                    ? -thickness.exteriorThickness
                    : -thickness.carcaseThickness,
                bevelEnabled: false,
            };

            const extrudeGeometry = new THREE.ExtrudeGeometry(
                shape,
                extrudeSettings
            );

            const geometry = new THREE.EdgesGeometry(extrudeGeometry);

            segmentMaterial.visible = false;

            if (showTexture) {
                // Reuse the already-created extrudeGeometry instead of creating a new one
                const {boxGeometry, size} = generateBoxGeometry({
                    extrudeGeometry,
                });

                const textureUrl = isExteriorMaterial
                    ? materialImage
                    : carcaseMaterialImage;

                const edgeTextureUrl = isExteriorMaterial
                    ? materialEdgeImage
                    : carcaseMaterialEdgeImage;

                // White placeholder immediately; swap to textured material once
                // images finish decoding (never render unloaded maps as black).
                const placeholderMat = new THREE.MeshStandardMaterial({
                    color: '#F7F8F5',
                });
                // Match the textured materials' opacity, so hidden panels
                // stay see-through when their texture never loads.
                if (hideSgement) {
                    reduceMaterialOpacity(
                        placeholderMat,
                        partiallyTransparentSegment ? 0.8 : undefined
                    );
                }
                if (nonSupplyCarcase) {
                    reduceMaterialOpacity(placeholderMat, 0.3, false);
                }
                const mesh = new THREE.Mesh(boxGeometry, placeholderMat);
                applyWallFlushOutlineFix(mesh);
                mesh.position.copy(size);
                panelGroup.add(mesh);
                addDragEffectOnClick(mesh, segmentMaterial);

                if (isWireframeMode) {
                    mesh.visible = false;
                }

                segmentMaterial.visible = true;

                if (!textureUrl || FORCE_FALLBACK_MATERIAL) {
                    notifyManagerInstantLoad(textureLoader, 'panel:no-texture');
                } else {
                    void trackTextureWork(
                        (async () => {
                            try {
                                const texture = await loadTexture(
                                    textureLoader,
                                    textureUrl
                                );
                                texture.generateMipmaps = true;
                                texture.minFilter =
                                    THREE.LinearMipmapLinearFilter;
                                texture.magFilter = THREE.LinearFilter;

                                if (
                                    (!horizontalGrainExt &&
                                        isExteriorMaterial) ||
                                    (!horizontalGrainCarc &&
                                        !isExteriorMaterial &&
                                        !isFrontRangehoodVent) ||
                                    (isFrontRangehoodVent &&
                                        horizontalGrainCarc)
                                ) {
                                    texture.rotation = (Math.PI / 4) * 2;
                                    texture.center = new THREE.Vector2(
                                        0.5,
                                        0.5
                                    );
                                    texture.needsUpdate = true;
                                }

                                let material:
                                    | THREE.MeshStandardMaterial
                                    | THREE.MeshStandardMaterial[] =
                                    new THREE.MeshStandardMaterial({
                                        ...(shouldApplyMaterial
                                            ? {map: texture}
                                            : {color: '#fff'}),
                                    });

                                if (shouldApplyMaterial) {
                                    let edgeTexture = null;

                                    try {
                                        edgeTexture = await loadTexture(
                                            textureLoader,
                                            edgeTextureUrl
                                        );
                                    } catch {}

                                    material = generateMaterials({
                                        plainWhiteColor,
                                        edgeTexture,
                                        texture,
                                        isExteriorMaterial,
                                        shouldApplyEdging,
                                        exteriorSubstrateMaterial,
                                        includeHardware,
                                        carcaseSubstrateMaterial,
                                        hideSgement,
                                        partiallyTransparentSegment,
                                        nonSupplyCarcase,
                                        operationCount,
                                        edging,
                                        isFrontRangehoodVent,
                                        withTopEdging,
                                        dynamicExteriorEdging,
                                        isEdgedBottom,
                                        noEdging,
                                        withFrontEdging,
                                        flipSubstrate,
                                        withBackEdging,
                                    });
                                }

                                (mesh as THREE.Mesh).material = material;
                                // The textured materials replace the
                                // placeholder, so re-apply its depth offset
                                // or the outlines z-fight with the faces.
                                applyWallFlushOutlineFix(mesh);

                                if (shouldApplyEdgingParam) {
                                    mesh.userData.ignoreOverlap = true;
                                }

                                if (shelfLabel && showShelfLabel) {
                                    queueMicrotask(() => {
                                        renderLabel({
                                            shelfLabel,
                                            showShelfLabel,
                                            shape,
                                            extrudeSettings,
                                            thickness,
                                            lineMesh,
                                            isLeftReturnProduct,
                                            isRightReturnProduct,
                                        });
                                    });
                                }
                            } catch {
                                // Keep white placeholder on failure.
                            }
                        })()
                    );
                }
            } else {
                segmentMaterial.visible = true;
            }

            const lineMesh = new THREE.LineSegments(geometry, segmentMaterial);

            if (fadeSegmentLines) {
                attachSegmentLineWireframeToggle(lineMesh);
            }

            if (!showTexture) {
                if (shelfLabel && showShelfLabel) {
                    // Reuse the existing extrudeGeometry instead of creating a new one
                    const box = new THREE.Box3().setFromObject(
                        new THREE.Mesh(extrudeGeometry)
                    );
                    const size = new THREE.Vector3();
                    box.getSize(size);
                    box.getCenter(size);

                    addLabelMesh(
                        shelfLabel,
                        size,
                        thickness.carcaseThickness,
                        lineMesh,
                        isLeftReturnProduct,
                        isRightReturnProduct
                    );
                }
            }

            if (hideSgement) {
                // With textures shown, keep hidden outlines no darker than
                // the faded outlines on the other panels; in wireframe mode
                // the outlines are the only thing drawn.
                const setHiddenLineOpacity = (isWireframe: boolean) =>
                    reduceMaterialOpacity(
                        lineMesh.material,
                        partiallyTransparentSegment &&
                            (!showTexture || isWireframe)
                            ? 0.8
                            : undefined
                    );

                setHiddenLineOpacity(isWireframeMode);

                if (showTexture) {
                    lineMesh.userData.onToggleWireframe = setHiddenLineOpacity;
                }
            }

            if (nonSupplyCarcase) {
                reduceMaterialOpacity(lineMesh.material);
            }
            panelGroup.add(lineMesh);
        }
    }

    panelGroup.rotateZ(Math.PI / 2);

    if (operationsGroup.MfgOrientationID === undefined) {
        panelGroup.scale.setY(-1);
    }

    panelGroup.scale.setZ(-1);

    const ret = new THREE.Group();
    ret.add(panelGroup);

    if (!isExteriorMaterial && thickness.centerOffset) {
        ret.position.z = thickness.centerOffset;
    }

    return ret;
};

const disposeMesh = (mesh: THREE.Mesh): void => {
    if (mesh.geometry) {
        mesh.geometry.dispose();
    }

    if (mesh.material) {
        const materials = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
        materials.forEach(
            (material: THREE.Material & {map: {dispose: () => void}}) => {
                if (material.map) material.map.dispose();

                material.dispose();
            }
        );
    }
};

const disposeThreeObject = (object: THREE.Object3D): void => {
    if (!object) return;

    object?.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
            disposeMesh(child as THREE.Mesh);
        }
    });

    if (object.parent) {
        object.parent.remove(object);
    }
};

export const disposeThreeScene = (
    scene: THREE.Scene,
    renderer: THREE.WebGLRenderer
): void => {
    if (scene) {
        scene?.traverse((object: THREE.Object3D) => {
            disposeThreeObject(object);
        });

        scene.clear();
    }

    if (renderer) {
        renderer.dispose();
    }
};
